import { FlowMEChangeContext } from ".";
import type { onboardingApiSpec } from "@wingspanhq/wingspan-api-types";
import { FormDataAccountType } from "../../components/FormAccountType";
import { CertifyTaxPayerInfo } from "../../steps/CertifyTaxPayerInfo";
import { StepImmaterialUpdateBusinessLegalName } from "./steps/StepImmaterialUpdateBusinessLegalName";
import { StepImmaterialUpdateIndividualLegalName } from "./steps/StepImmaterialUpdateIndividualLegalName";
import { StepImmaterialUpdateReview } from "./steps/StepImmaterialUpdateReview";
import { StepMaterialUpdateAccountType } from "./steps/StepMaterialUpdateAccountType";
import { StepMaterialUpdateBusinessInformation } from "./steps/StepMaterialUpdateBusinessInformation";
import { StepMaterialUpdatePersonalInformation } from "./steps/StepMaterialUpdatePersonalInformation";
import { StepMaterialUpdateRepresentative } from "./steps/StepMaterialUpdateRepresentative";
import { StepMaterialUpdateReview } from "./steps/StepMaterialUpdateReview";
import { StepReason } from "./steps/StepReason";
import { StepMaterialInternationalUSPersonInformation } from "./steps/StepMaterialInternationalUSPersonInformation";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

function getMaterialUpdateSteps(
  accountType: FormDataAccountType["type"],
  isInternationalUSPerson: boolean
) {
  if (accountType === "Business") {
    return [
      {
        title: "Account type",
        slug: "type",
        component: StepMaterialUpdateAccountType
      },
      {
        title: "Business information",
        slug: "business",
        component: StepMaterialUpdateBusinessInformation
      },
      {
        title: "Representative",
        slug: "representative",
        component: StepMaterialUpdateRepresentative
      },
      {
        title: "Review & submit",
        slug: "review",
        component: StepMaterialUpdateReview
      }
    ];
  }

  return [
    {
      title: "Account type",
      slug: "type",
      component: StepMaterialUpdateAccountType
    },
    {
      title: "Personal information",
      slug: "personal",
      component: StepMaterialUpdatePersonalInformation
    },
    ...(isInternationalUSPerson
      ? [
          {
            title: "U.S. person information",
            slug: "international-us-person-information",
            component: StepMaterialInternationalUSPersonInformation
          }
        ]
      : []),
    {
      title: "Review & submit",
      slug: "review",
      component: StepMaterialUpdateReview
    }
  ];
}

export function getSteps(
  context: FlowMEChangeContext,
  currentMonitoredEntity?: onboardingApiSpec.CustomerEntity,
  initialVerificationStatus?: onboardingApiSpec.VerificationStatus,
  currentVerificationStatus?: onboardingApiSpec.VerificationStatus
): FlowStep[] {
  const accountType =
    context.accountType?.type || currentMonitoredEntity?.type || "Individual";

  const isMaterialChange =
    context.reason?.changeType &&
    ["business-structure", "tax-id", "tax-residency"].includes(
      context.reason?.changeType
    );

  const mutationComplete =
    context.materialChangeCompleted || context.nameChanged;

  const isInternationalUSPerson = !!context.accountType?.isUsPerson;

  const materialUpdateStep: FlowStep = {
    title: "Update taxpayer info",
    slug: "update",
    steps: getMaterialUpdateSteps(accountType, isInternationalUSPerson),
    status:
      mutationComplete && currentVerificationStatus
        ? currentVerificationStatus === "Verified"
          ? "Complete"
          : "Pending"
        : "None",
    result: {
      Pending: {
        title: "Your taxpayer info update is pending review",
        description:
          "The update to your taxpayer information is being reviewed. Keep an eye out for updates from us and if additional actions are required. You will be notified by email and on your home page.\n\nPlease note that, if necessary, your account information will be updated by our team to match official documents provided.\n\nClick continue for the next step."
      }
    }
  };

  const immaterialUpdateStep = {
    title: "Update taxpayer info",
    slug: "update",
    steps: [
      {
        title: "Update taxpayer info",
        slug: "type",
        component:
          currentMonitoredEntity?.type === "Individual"
            ? StepImmaterialUpdateIndividualLegalName
            : StepImmaterialUpdateBusinessLegalName
      },
      {
        title: "Personal information",
        slug: "personal",
        component: StepImmaterialUpdateReview
      }
    ]
  };

  const reasonStep = {
    title: "Reason for change",
    slug: "reason",
    component: StepReason,
    status: !!context.reason ? "Complete" : "None"
  };

  const cerifyStep = {
    title: "Certify taxpayer info",
    slug: "certify",
    component: CertifyTaxPayerInfo
  };

  if (initialVerificationStatus === "Verified") {
    return [
      reasonStep,
      isMaterialChange ? materialUpdateStep : immaterialUpdateStep,
      cerifyStep
    ];
  }

  return [materialUpdateStep, cerifyStep];
}
